import React, { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { HotTable } from '@handsontable/react-wrapper'
import { HOT_LICENSE_KEY } from '../utils/handsontableLicense.js'
import { hotNumericFormat } from '../utils/handsontableConfig.js'
import { sanitizeHandsontableHtml } from '../utils/handsontableSanitizer.js'

// Schedule Capex Spend modal (capex-developer-note §9, §17). A per-row phasing
// override for Fixed Amount capex: enter an Annual Totals row or a Monthly
// Amounts grid across the analysis years; growth (the row's profile) is applied
// cumulatively from Y2. Mirrors the prototype CapexSchedulerModal — Bootstrap
// shell + Handsontable grids, like TenantSelectorModal.

const MONTHS_PER_YEAR = 12

const ALLOCATION_OPTIONS = [
  { value: 'spreadAcross12Months', label: 'Spread across 12 months' },
  { value: 'lumpSumAtBeginningOfPeriod', label: 'Lump sum at start of period' }
]

function normalizeAmount(value) {
  const n = Number(value)
  return Number.isFinite(n) && n > 0 ? n : 0
}

// Per-year growth rate (%) with carry-forward, matching the Ruby Growth module.
function rateForYear(rates, year) {
  if (!rates) return 0
  const keys = Object.keys(rates).map(Number).filter((k) => k <= year)
  if (!keys.length) return 0
  const v = Number(rates[String(Math.max(...keys))])
  return Number.isFinite(v) ? v : 0
}

// Cumulative factor per analysis year (0-based; Y1 == 1), same as the backend.
function annualFactors(rates, years) {
  const out = []
  let factor = 1
  for (let i = 0; i < years; i += 1) {
    out.push(factor)
    factor *= 1 + rateForYear(rates, i + 1) / 100
  }
  return out
}

function emptyScheduler(years, basis = 'annualTotals') {
  return {
    enabled: false,
    basis,
    annualAllocationMethod: 'spreadAcross12Months',
    annualTotals: { amountsByYear: Array.from({ length: years }, () => 0) },
    monthlyAmounts: { amountsByYearMonth: Array.from({ length: years }, () => Array.from({ length: MONTHS_PER_YEAR }, () => 0)) },
    totalEntered: 0,
    totalAfterGrowth: 0
  }
}

// Re-shape a stored (possibly stale-length) scheduler to the current analysis length.
function resizeScheduler(scheduler, years) {
  const base = emptyScheduler(years, scheduler?.basis === 'monthlyAmounts' ? 'monthlyAmounts' : 'annualTotals')
  if (!scheduler) return base
  const annual = scheduler.annualTotals?.amountsByYear || []
  const monthly = scheduler.monthlyAmounts?.amountsByYearMonth || []
  return {
    ...base,
    enabled: !!scheduler.enabled,
    annualAllocationMethod: scheduler.annualAllocationMethod === 'lumpSumAtBeginningOfPeriod'
      ? 'lumpSumAtBeginningOfPeriod' : 'spreadAcross12Months',
    annualTotals: { amountsByYear: base.annualTotals.amountsByYear.map((_, i) => normalizeAmount(annual[i])) },
    monthlyAmounts: {
      amountsByYearMonth: base.monthlyAmounts.amountsByYearMonth.map((row, y) =>
        row.map((_, m) => normalizeAmount(monthly[y]?.[m])))
    }
  }
}

function formatSummary(value) {
  return Number(value || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })
}

function formatInput(value) {
  return value > 0 ? value : ''
}

// Year-end label for analysis year n (1-based): valuation date + 12n months.
// yearOnly drops the month (used by the Monthly Amounts view, where the month is
// already shown in the left-hand row labels — EVA-216 item 4).
function yearEndLabel(valuationDate, n, yearOnly = false) {
  if (!valuationDate) return `Y${n}`
  const start = new Date(`${valuationDate}T00:00:00Z`)
  if (Number.isNaN(start.getTime())) return `Y${n}`
  const end = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + MONTHS_PER_YEAR * n, 1))
  const opts = yearOnly
    ? { year: 'numeric', timeZone: 'UTC' }
    : { month: 'short', year: 'numeric', timeZone: 'UTC' }
  return end.toLocaleDateString('en-US', opts)
}

// Month label for model month i (0-based). Model month 0 is the month AFTER the
// valuation date (period_ends are offset +1), so monthIndex 0 → valuation + 1.
function monthLabel(valuationDate, monthIndex) {
  if (!valuationDate) return `M${monthIndex + 1}`
  const start = new Date(`${valuationDate}T00:00:00Z`)
  if (Number.isNaN(start.getTime())) return `M${monthIndex + 1}`
  const d = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1 + monthIndex, 1))
  return d.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' })
}

export default function CapexSchedulerModal({
  row, years, valuationDate, growthProfiles = [], onSave, onClear, onCancel
}) {
  const [draft, setDraft] = useState(() => resizeScheduler(row?.scheduler, years))
  const [growthApplied, setGrowthApplied] = useState(row?.growth_rate || 'Expense Inflation')
  const [validation, setValidation] = useState('')

  // Seed the local draft + growth selection when the modal opens for a row (or the
  // analysis length changes) — keyed on row.id ONLY, never the scheduler object
  // identity, so a background refetch that re-parses rawData while the modal is
  // open can't wipe the unsaved draft the user is typing.
  useEffect(() => {
    setDraft(resizeScheduler(row?.scheduler, years))
    setGrowthApplied(row?.growth_rate || 'Expense Inflation')
    setValidation('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row?.id, years])

  const rates = useMemo(() => {
    const profile = growthProfiles.find((p) => p.name === growthApplied)
    return profile?.rates || {}
  }, [growthProfiles, growthApplied])

  const yearKeys = useMemo(() => Array.from({ length: years }, (_, i) => `y${i}`), [years])

  const totals = useMemo(() => {
    const factors = annualFactors(rates, years)
    const enteredByYear = draft.basis === 'annualTotals'
      ? draft.annualTotals.amountsByYear.map(normalizeAmount)
      : draft.monthlyAmounts.amountsByYearMonth.map((m) => m.reduce((s, v) => s + normalizeAmount(v), 0))
    const afterGrowthByYear = enteredByYear.map((amount, y) => amount * (factors[y] || 1))
    return {
      enteredByYear,
      afterGrowthByYear,
      appliedRateByYear: enteredByYear.map((_, y) => (y === 0 ? null : rateForYear(rates, y))),
      totalEntered: enteredByYear.reduce((s, v) => s + v, 0),
      totalAfterGrowth: afterGrowthByYear.reduce((s, v) => s + v, 0)
    }
  }, [draft, rates, years])

  const columns = useMemo(() => [
    { data: 'rowLabel', readOnly: true, width: 160 },
    ...yearKeys.map((key) => ({ data: key, type: 'numeric', ...hotNumericFormat(2, true) }))
  ], [yearKeys])

  const nestedHeaders = useMemo(() => {
    const yearOnly = draft.basis === 'monthlyAmounts'
    return [
      ['', ...yearKeys.map((_, i) => `Y${i + 1}`)],
      ['', ...yearKeys.map((_, i) => yearEndLabel(valuationDate, i + 1, yearOnly))]
    ]
  }, [yearKeys, valuationDate, draft.basis])

  const appliedRow = (rowType) => ({
    rowLabel: 'Applied Growth Rate', rowType,
    ...Object.fromEntries(yearKeys.map((key, i) => [key, totals.appliedRateByYear[i] === null ? '—' : `${totals.appliedRateByYear[i].toFixed(2)}%`]))
  })
  const afterGrowthRow = () => ({
    rowLabel: 'Total After Growth', rowType: 'afterGrowth',
    ...Object.fromEntries(yearKeys.map((key, i) => [key, totals.afterGrowthByYear[i] || 0]))
  })

  const annualData = useMemo(() => ([
    {
      rowLabel: 'Annual Total', rowType: 'annualTotal',
      ...Object.fromEntries(yearKeys.map((key, i) => [key, formatInput(draft.annualTotals.amountsByYear[i])]))
    },
    appliedRow('appliedRate'),
    afterGrowthRow()
  ]), [draft, yearKeys, totals])

  const monthlyData = useMemo(() => ([
    ...Array.from({ length: MONTHS_PER_YEAR }, (_, m) => ({
      rowLabel: monthLabel(valuationDate, m), rowType: 'month',
      ...Object.fromEntries(yearKeys.map((key, y) => [key, formatInput(draft.monthlyAmounts.amountsByYearMonth[y]?.[m] ?? 0)]))
    })),
    {
      rowLabel: 'Total Entered', rowType: 'totalEntered',
      ...Object.fromEntries(yearKeys.map((key, i) => [key, totals.enteredByYear[i] || 0]))
    },
    appliedRow('appliedRate'),
    afterGrowthRow()
  ]), [draft, yearKeys, valuationDate, totals])

  const updateBasis = (basis) => { setDraft((prev) => ({ ...prev, basis })); setValidation('') }
  const updateAllocation = (annualAllocationMethod) => setDraft((prev) => ({ ...prev, annualAllocationMethod }))

  const handleAnnualChange = (changes, source) => {
    if (!changes || source === 'loadData') return
    setDraft((prev) => {
      const next = resizeScheduler(prev, years)
      next.basis = prev.basis
      next.annualAllocationMethod = prev.annualAllocationMethod
      changes.forEach(([r, key, , newVal]) => {
        if (r !== 0 || typeof key !== 'string' || !key.startsWith('y')) return
        const y = Number(key.slice(1))
        if (Number.isInteger(y) && y >= 0 && y < years) next.annualTotals.amountsByYear[y] = normalizeAmount(newVal)
      })
      return next
    })
    setValidation('')
  }

  const handleMonthlyChange = (changes, source) => {
    if (!changes || source === 'loadData') return
    setDraft((prev) => {
      const next = resizeScheduler(prev, years)
      next.basis = prev.basis
      next.annualAllocationMethod = prev.annualAllocationMethod
      changes.forEach(([r, key, , newVal]) => {
        if (typeof r !== 'number' || r < 0 || r >= MONTHS_PER_YEAR || typeof key !== 'string' || !key.startsWith('y')) return
        const y = Number(key.slice(1))
        if (Number.isInteger(y) && y >= 0 && y < years) next.monthlyAmounts.amountsByYearMonth[y][r] = normalizeAmount(newVal)
      })
      return next
    })
    setValidation('')
  }

  const handleSave = () => {
    if (totals.totalEntered <= 0) {
      setValidation('Enter at least one scheduled amount before saving, or clear the schedule.')
      return
    }
    onSave({
      ...draft,
      enabled: true,
      totalEntered: totals.totalEntered,
      totalAfterGrowth: totals.totalAfterGrowth
    }, growthApplied)
  }

  const gridCells = (isMonthly) => (r, col) => {
    const props = {}
    const summaryStart = isMonthly ? MONTHS_PER_YEAR : 1
    if (r >= summaryStart) { props.readOnly = true; props.className = 'htRight text-muted' }
    if (col === 0) {
      props.readOnly = true
      // Right-align the "Annual Total" input-row label so it matches the
      // right-aligned summary rows below it (EVA-216 item 3).
      if (!isMonthly && r === 0) props.className = 'htRight'
    }
    return props
  }

  return createPortal(
    <div className="modal d-block" tabIndex="-1" role="dialog" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-xl modal-dialog-scrollable" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <div>
              <h5 className="modal-title">Schedule Capex Spend</h5>
              <p className="text-muted small mb-0">Project: {(row?.name || '').trim() || 'Untitled capex project'}</p>
            </div>
            <button type="button" className="btn-close" aria-label="Close" onClick={onCancel}></button>
          </div>
          <div className="modal-body">
            <div className="row g-3 mb-3">
              <div className="col-4">
                <div className="border rounded p-2 h-100">
                  <div className="text-muted small">Total Entered</div>
                  <strong>{formatSummary(totals.totalEntered)}</strong>
                </div>
              </div>
              <div className="col-4">
                <div className="border rounded p-2 h-100">
                  <div className="text-muted small">Total After Growth</div>
                  <strong>{formatSummary(totals.totalAfterGrowth)}</strong>
                </div>
              </div>
              <div className="col-4">
                <div className="border rounded p-2 h-100">
                  <label className="text-muted small d-block">Growth Applied</label>
                  <select className="form-select form-select-sm" value={growthApplied}
                          onChange={(e) => setGrowthApplied(e.target.value)}>
                    {growthProfiles.map((p) => <option key={p.name} value={p.name}>{p.name}</option>)}
                  </select>
                </div>
              </div>
            </div>

            <div className="btn-group mb-3" role="group" aria-label="Schedule Basis">
              <button type="button" className={`btn btn-sm ${draft.basis === 'annualTotals' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => updateBasis('annualTotals')}>Annual Totals</button>
              <button type="button" className={`btn btn-sm ${draft.basis === 'monthlyAmounts' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => updateBasis('monthlyAmounts')}>Monthly Amounts</button>
            </div>

            {draft.basis === 'annualTotals' && (
              <div className="mb-3" style={{ maxWidth: '22rem' }}>
                <label className="form-label small mb-1">Annual Allocation</label>
                <select className="form-select form-select-sm" value={draft.annualAllocationMethod}
                        onChange={(e) => updateAllocation(e.target.value)}>
                  {ALLOCATION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            )}

            <div className="cash-flow-table-container capex-scheduler-table">
              {draft.basis === 'annualTotals' ? (
                <HotTable data={annualData} columns={columns} nestedHeaders={nestedHeaders} rowHeaders={false}
                          themeName="ht-theme-horizon" licenseKey={HOT_LICENSE_KEY}
                          sanitizer={sanitizeHandsontableHtml}
                          width="100%" height={220} stretchH="all" manualColumnResize
                          afterChange={handleAnnualChange} cells={gridCells(false)} />
              ) : (
                <HotTable data={monthlyData} columns={columns} nestedHeaders={nestedHeaders} rowHeaders={false}
                          themeName="ht-theme-horizon" licenseKey={HOT_LICENSE_KEY}
                          sanitizer={sanitizeHandsontableHtml}
                          width="100%" height={420} stretchH="all" manualColumnResize
                          afterChange={handleMonthlyChange} cells={gridCells(true)} />
              )}
            </div>

            <p className="text-muted small mt-2 mb-0">
              Y1 is ungrown. Growth shown is the rate applied to that cash-flow year. Y2 uses the Y1 growth rate,
              Y3 uses the Y2 growth rate, and so on.
            </p>
            {validation && <div className="text-danger small mt-1">{validation}</div>}
          </div>
          <div className="modal-footer justify-content-between">
            <button type="button" className="btn btn-outline-danger btn-sm" onClick={onClear}>Clear Schedule</button>
            <div>
              <button type="button" className="btn btn-light btn-sm me-2" onClick={onCancel}>Cancel</button>
              <button type="button" className="btn btn-primary btn-sm" onClick={handleSave}>Save Schedule</button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}
